import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { API_URL, pathVideoCall } from "../../config/constants";
import socket from "../../socket";
import PatientSidebar from "../../components/PatientSidebar";
import CaregiverChat from "../../components/caregiverChat";
import VideoConsultationPanel from "../../components/videoConsultationPanel";
import JoinConsultationModal from "../../components/joinConsultationModal";
import avatarMap from "../../assets/avatars";
import therapistAvatar from "../../assets/therapist.png";
import TopBar from "../../components/TopBar";
import {
  Layout, Main, PageTitle, Columns, ChatColumn, SideColumn, SidePanel, SidePanelTitle,
  TherapistRow, TherapistAvatar, TherapistName, MessageBox,
} from "./styles";

const PatientCaregiverSupport = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const token = localStorage.getItem("token");
  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");

  const [therapist, setTherapist] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [joinTarget, setJoinTarget] = useState(null);

  // Abrir el chat marca como leídos los mensajes del terapeuta, que es lo que
  // apaga la insignia de la barra lateral.
  const markRead = useCallback((therapistId) => {
    if (!therapistId) return;
    fetch(`${API_URL}/messages/${therapistId}/read`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    }).catch(() => {});
  }, [token]);

  const loadSessions = useCallback(() => {
    fetch(`${API_URL}/sessions/upcoming`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => setSessions(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [token]);

  useEffect(() => {
    fetch(`${API_URL}/me/therapist`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.error) return;
        setTherapist(data);
        markRead(data.id);
      })
      .catch(() => {});

    loadSessions();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps


  // Si llega un mensaje con el chat abierto, ya está leído.
  useEffect(() => {
    const onMessage = (msg) => {
      if (therapist && msg.sender_id === therapist.id) markRead(therapist.id);
    };
    socket.on("receiveMessage", onMessage);
    return () => socket.off("receiveMessage", onMessage);
  }, [therapist, markRead]);

  // El terapeuta puede editar el mensaje fijo mientras esta pantalla está abierta.
  useEffect(() => {
    const onMessageUpdated = (data) =>
      setTherapist((prev) => (prev ? { ...prev, therapist_message: data.therapist_message } : prev));
    socket.on("therapistMessageUpdated", onMessageUpdated);
    return () => socket.off("therapistMessageUpdated", onMessageUpdated);
  }, []);

  useEffect(() => {
    const onSessionUpdated = () => loadSessions();
    socket.on("sessionUpdated", onSessionUpdated);
    return () => socket.off("sessionUpdated", onSessionUpdated);
  }, [loadSessions]);

  // Accept / Decline desde la tarjeta de propuesta del chat.
  const respondProposal = async (sessionId, action) => {
    try {
      const res = await fetch(`${API_URL}/sessions/${sessionId}/${action}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      toast.success(t(`consultation.toast${action === "accept" ? "Accepted" : "Declined"}`));
      loadSessions();
    } catch {
      toast.error(t("consultation.toastError"));
    }
  };

  const cancelConsultation = async (sessionId) => {
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/sessions/${sessionId}/cancel`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      toast.success(t("consultation.toastCancelled"));
      loadSessions();
    } catch {
      toast.error(t("consultation.toastError"));
    } finally {
      setBusy(false);
    }
  };

  // El paciente entra a la sala y anuncia que espera; quien inicia la llamada
  // es siempre el terapeuta. Si ambos emitieran oferta a la vez, ninguna se
  // contestaría. `waitForSessionId` (y no `sessionId`) evita disparar la
  // llamada automática del lado del paciente.
  const joinCall = (session) => {
    setJoinTarget(null);
    history.push(pathVideoCall, { waitForSessionId: session.id });
  };

  return (
    <Layout>
      <TopBar />

      <PatientSidebar activeNav="caregiver" />

      <Main>
        <PageTitle>{t("caregiver.title")}</PageTitle>

        <Columns>
          <ChatColumn>
            <CaregiverChat
              title={
                therapist
                  ? t("caregiver.chatWith", { code: therapist.username })
                  : t("caregiver.noTherapist")
              }
              currentUserId={currentUser.id}
              otherUserId={therapist?.id}
              otherAvatar={therapistAvatar}
              ownAvatar={avatarMap[currentUser.avatar_url] || Object.values(avatarMap)[0]}
              canRespondToProposals
              onRespondProposal={respondProposal}
            />
          </ChatColumn>

          {(therapist || sessions.length > 0) && (
            <SideColumn>
              {therapist && (
                <SidePanel>
                  <SidePanelTitle>{t("caregiver.therapistDetails")}</SidePanelTitle>
                  <TherapistRow>
                    <TherapistAvatar src={therapistAvatar} alt="" />
                    <TherapistName>
                      {therapist.display_name || therapist.username}
                    </TherapistName>
                  </TherapistRow>
                  {therapist.therapist_message && (
                    <MessageBox>{therapist.therapist_message}</MessageBox>
                  )}
                </SidePanel>
              )}

              {/* Cada cita trae su propia tarjeta: no necesita rótulo aparte */}
              <VideoConsultationPanel
                sessions={sessions}
                busy={busy}
                fill
                onJoin={setJoinTarget}
                onCancel={cancelConsultation}
              />
            </SideColumn>
          )}
        </Columns>
      </Main>

      {joinTarget && therapist && (
        <JoinConsultationModal
          session={joinTarget}
          withLabel={therapist.username}
          roleLabel={t("consultation.readyModal.roleTherapist")}
          onClose={() => setJoinTarget(null)}
          onJoin={() => joinCall(joinTarget)}
        />
      )}
    </Layout>
  );
};

export default PatientCaregiverSupport;
